import NormalProgressBar from 'components/atoms/ProgressBar/NormalProgressBar';
import { IStoryData } from 'interfaces/Story.interface';
import React, { FC, RefObject, useEffect, useRef, useState } from 'react';
import './StoryBody.css';
import {
  CloseIconCont,
  CustomLink,
  Img,
  LeftArrow,
  LogoCont,
  MainBody,
  ProgressBarCont,
  RightArrow,
  UserDetailCont,
  UsernameText,
  Video,
  VideoCont,
} from './StoryBody.style';

interface IStoryBody {
  data: IStoryData;
  hideStoryUI: () => void;
  increaseStoryUI: () => void;
  decreaseStoryUI: () => void;
}

const StoryBody: FC<IStoryBody> = ({
  data,
  hideStoryUI,
  increaseStoryUI,
  decreaseStoryUI,
}) => {
  const logo = require('assets/logo.png');
  const plusIcon = require('assets/icons/plus-icon.svg');
  const rightArrow = require('assets/icons/right-arrow.svg');

  const currentProgress = useRef<number>(0);
  const increaseProgressInterval: { current: NodeJS.Timer | null } =
    useRef(null);
  const pauseProgress = useRef<boolean>(false);

  const [progress, setProgress] = useState<number>(currentProgress.current);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const currentElem = useRef<HTMLVideoElement | HTMLImageElement>(null);

  useEffect(() => {
    if (currentElem.current) {
      if (currentElem.current.nodeName === 'VIDEO') {
        const videoElem = currentElem.current as HTMLVideoElement;
        videoElem.addEventListener('timeupdate', () =>
          handleVideoProgress(videoElem)
        );
        videoElem.addEventListener('ended', () => increaseActiveIndex());
        if (pauseProgress.current) {
          videoElem.pause();
        }
        return () => {
          videoElem.removeEventListener('timeupdate', () =>
            handleVideoProgress(videoElem)
          );
          videoElem.removeEventListener('ended', () => increaseActiveIndex());
        };
      } else if (currentElem.current.nodeName === 'IMG') {
        setProgress(0);
        handleImageProgress();
      }
    }
    //eslint-disable-next-line
  }, [currentElem, activeIndex]);

  function handleVideoProgress(videoElem: HTMLVideoElement) {
    const totalDuration = videoElem.duration;
    const progress = (videoElem.currentTime / totalDuration) * 100;
    if (!pauseProgress.current) {
      setProgress(progress);
    }
  }

  function handleImageProgress() {
    increaseProgressInterval.current = setInterval(() => {
      if (!pauseProgress.current) {
        currentProgress.current = currentProgress.current + 1.5;
        setProgress(currentProgress.current);
        if (currentProgress.current > 100) {
          currentProgress.current = 0;
          handleActiveIndex(activeIndex + 1);
        }
      }
    }, 100);
  }

  const handleActiveIndex = (index: number) => {
    if (increaseProgressInterval.current) {
      clearInterval(increaseProgressInterval.current);
    }
    if (index > data.stories.length - 1) {
      setActiveIndex(0);
      setProgress(0);
      increaseStoryUI();
      return;
    } else if (index < 0) {
      setProgress(0);
      setActiveIndex(0);
      decreaseStoryUI();
      return;
    }
    setProgress(0);
    setActiveIndex(index);
  };

  const increaseActiveIndex = () => {
    const nextIndex = activeIndex + 1;
    handleActiveIndex(nextIndex);
  };

  const toggleProgress = () => {
    if (pauseProgress.current) {
      if (currentElem.current && currentElem.current.nodeName === 'VIDEO') {
        const videoElem = currentElem.current as HTMLVideoElement;
        videoElem.play();
      }
      pauseProgress.current = false;
    } else {
      if (currentElem.current && currentElem.current.nodeName === 'VIDEO') {
        const videoElem = currentElem.current as HTMLVideoElement;
        videoElem.pause();
      }
      pauseProgress.current = true;
    }
  };

  return (
    <MainBody>
      <LogoCont>
        <img src={logo} alt="logo" height={'100%'} />
      </LogoCont>
      <CloseIconCont onClick={hideStoryUI}>
        <img
          src={plusIcon.default}
          alt="icon"
          width={'100%'}
          height={'100%'}
          style={{
            transform: 'rotate(45deg)',
          }}
        />
      </CloseIconCont>
      <VideoCont>
        <ProgressBarCont>
          {data.stories.map((story, i) => (
            <NormalProgressBar
              key={i}
              progress={`${i === activeIndex ? progress : 0}`}
            />
          ))}
        </ProgressBarCont>

        <UserDetailCont>
          <img src={data.image} alt="profile pic" />
          <UsernameText>@{data.username}</UsernameText>
        </UserDetailCont>

        {data.stories[activeIndex].type === 'image' ? (
          <>
            <Img
              ref={currentElem as RefObject<HTMLImageElement>}
              src={data.stories[activeIndex].src || ''}
              alt={`${data.username}'s Story`}
              onClick={toggleProgress}
            />
            {data.stories[activeIndex].url && (
              <CustomLink to={`${data.stories[activeIndex].url}`}>
                View NFT
              </CustomLink>
            )}
          </>
        ) : (
          <Video
            ref={currentElem as RefObject<HTMLVideoElement>}
            src={data.stories[activeIndex].src || ''}
            muted
            autoPlay
            controls={false}
            onClick={toggleProgress}
          />
        )}

        <LeftArrow
          src={rightArrow.default}
          alt="icon"
          onClick={() => handleActiveIndex(activeIndex - 1)}
        />

        <RightArrow
          src={rightArrow.default}
          alt="icon"
          onClick={() => handleActiveIndex(activeIndex + 1)}
        />
      </VideoCont>
    </MainBody>
  );
};

export default StoryBody;
